import { Icons } from '@/components/icons';
import TicketCuticle from '@/components/manage/TicketCuticle';
import PriceDisplay from '@/components/PriceDisplay';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import {
  activeTicketsProfileSelector,
  historyTicketsProfileSelector,
  profileDataSelector,
} from '@/features/profile/profileSelectors';
import { useBookingSearch } from '@/hooks/useBookingSearch';
import { TransText } from '@/i18n/trans/text';
import BookingNoItems from '@/routes/account/layout/BookingNoItems';
import TicketTripSummaries from '@/routes/account/tickets/TicketTripSummaries';
import { useSelector } from '@/store/utils';
import type { ProfileBookingTimeType } from '@/types/profile';
import { breakpoints } from '@/utils/breakpoints';
import { format } from '@/utils/dateTime';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TicketsListProps {
  timeType: ProfileBookingTimeType;
}

const TicketsList: FC<TicketsListProps> = ({ timeType }) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const profile = useSelector(profileDataSelector);
  const tickets = useSelector(
    timeType === 'active'
      ? activeTicketsProfileSelector
      : historyTicketsProfileSelector
  );

  const onBookingSearch = useBookingSearch();

  if (!tickets?.length) {
    return <BookingNoItems bookingType="tickets" timeType={timeType} />;
  }

  return (
    <div className="flex w-full flex-col gap-4 laptop:mt-2 laptop:max-w-[628px]">
      {tickets.map((ticket) => {
        const outboundTripsSummaries = (ticket.tripSummaries ?? []).filter(
          (trip) => trip.direction === 'OUTBOUND'
        );
        const inboundTripsSummaries = (ticket.tripSummaries ?? []).filter(
          (trip) => trip.direction === 'INBOUND'
        );
        const originTime =
          ticket.journeyOrigin?.time &&
          format(ticket.journeyOrigin.time, 'EEE, dd MMM yyyy');
        const destinationTime =
          ticket.journeyDestination?.time &&
          format(ticket.journeyDestination.time, 'EEE, dd MMM yyyy');
        const dateRange =
          originTime && destinationTime && originTime !== destinationTime
            ? `${originTime} - ${destinationTime}`
            : (originTime ?? destinationTime);

        return (
          <div key={ticket.id} className="flex flex-col">
            <div className="flex justify-between rounded-t-lg border-b border-primary bg-primary-pale p-3 laptop:p-4">
              <div className="flex flex-col gap-0.5 laptop:flex-row laptop:items-center laptop:gap-2">
                <Typography
                  variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
                  className="text-dark"
                >
                  <>
                    <TransText i18nKey="booking" />
                    &#58; {ticket.code}
                  </>
                </Typography>
                {dateRange && (
                  <Typography
                    variant={isLaptopOrBigger ? 'body1' : 'body2'}
                    className="text-neutral"
                  >
                    {dateRange}
                  </Typography>
                )}
              </div>
              <Typography
                variant={isLaptopOrBigger ? 'subtitle' : 'body1-bold'}
                className="text-primary"
              >
                <PriceDisplay amount={ticket.totalPrice?.amount} />
              </Typography>
            </div>
            <div className="flex flex-col gap-4 bg-white p-3 pb-6 text-dark laptop:flex-row laptop:p-4">
              <TicketTripSummaries
                title="outboundTrips"
                tripSummaries={outboundTripsSummaries}
                passengerCount={ticket.passengers?.length}
              />
              {Boolean(
                outboundTripsSummaries.length && inboundTripsSummaries.length
              ) && (
                <Divider
                  orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
                  className="border-neutral-pale"
                  grow={false}
                />
              )}
              <TicketTripSummaries
                title="returnTrips"
                tripSummaries={inboundTripsSummaries}
                passengerCount={ticket.passengers?.length}
              />
            </div>
            <TicketCuticle isVertical={false} />
            <div className="flex flex-col gap-2 bg-white p-3 pt-6 text-dark laptop:flex-row-reverse laptop:p-4">
              <Button
                data-testid="profile-booking-view-details"
                size="medium"
                variant="secondary"
                fullWidth
                onClick={() =>
                  onBookingSearch({
                    email: profile?.email,
                    bookingNumber: ticket.code,
                  })
                }
              >
                <TransText i18nKey="viewDetails" />
                <Icons.arrowRight width={20} height={20} />
              </Button>
              <Button
                data-testid="profile-booking-send-to-email"
                size="medium"
                variant="tertiary"
                fullWidth
              >
                <Icons.email width={20} height={20} />
                <TransText i18nKey="sendToEmail" />
              </Button>
              <Button
                data-testid="profile-booking-download"
                size="medium"
                variant="tertiary"
                fullWidth
              >
                <Icons.download width={20} height={20} />
                <TransText i18nKey="download" />
                <Icons.chevronDown height={16} width={16} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TicketsList;
